import { useEffect, useRef, useState } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { MEDIA, CONTACT } from "@/data/site";
import { EASE } from "@/lib/motion";

const D = 2.4; // hero content lands right after the title card clears

export const Hero = ({ onBrief }) => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const yImg = useTransform(scrollYProgress, [0, 1], ["0%", "18%"]);
  const yWord = useTransform(scrollYProgress, [0, 1], ["0%", "-40%"]);
  const fade = useTransform(scrollYProgress, [0, 0.8], [1, 0]);

  const [tilt, setTilt] = useState({ x: 0, y: 0 });
  const [frame, setFrame] = useState(0);

  useEffect(() => {
    const int = setInterval(() => setFrame((f) => f + 1), 42);
    return () => clearInterval(int);
  }, []);

  const onMove = (e) => {
    const w = window.innerWidth;
    const h = window.innerHeight;
    setTilt({ x: (e.clientX / w - 0.5) * 18, y: (e.clientY / h - 0.5) * 10 });
  };

  const tc = `00:0${Math.floor(frame / 1440) % 10}:${String(Math.floor(frame / 24) % 60).padStart(2, "0")}:${String(frame % 24).padStart(2, "0")}`;

  return (
    <section
      id="top"
      ref={ref}
      onMouseMove={onMove}
      data-testid="hero"
      className="relative min-h-[100svh] overflow-hidden"
    >
      <motion.div className="absolute inset-0" style={{ y: yImg, opacity: fade }}>
        <motion.img
          src={MEDIA.heroStill}
          alt=""
          loading="eager"
          initial={{ scale: 1.22, opacity: 0 }}
          animate={{ scale: 1.06, opacity: 0.42 }}
          transition={{ duration: 2.2, ease: EASE, delay: D - 0.6 }}
          className="h-full w-full object-cover grayscale"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-[#0a0a0a]/75 via-[#0a0a0a]/88 to-[#0a0a0a]" />
        <div className="scanlines absolute inset-0 opacity-60" />
      </motion.div>

      <motion.div
        className="pointer-events-none absolute inset-x-0 top-[16%] flex justify-center"
        style={{ y: yWord }}
      >
        <motion.span
          initial={{ opacity: 0, letterSpacing: "0.3em" }}
          animate={{ opacity: 1, letterSpacing: "-0.04em" }}
          transition={{ duration: 1.8, ease: EASE, delay: D - 0.4 }}
          className="select-none font-display text-[19vw] font-black leading-none text-transparent"
          style={{
            WebkitTextStroke: "1px rgba(229,229,229,0.08)",
            transform: `translate3d(${tilt.x * -1}px, ${tilt.y * -1}px, 0)`,
          }}
        >
          PORTFOLIO
        </motion.span>
      </motion.div>

      <div className="relative mx-auto flex min-h-[100svh] max-w-[1700px] flex-col justify-center px-5 pb-24 pt-28 md:px-10 md:pb-20">
        {/* identity block — video editor, stated not implied */}
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: EASE, delay: D - 0.2 }}
          className="mb-10 md:mb-14"
        >
          <div className="flex flex-wrap items-center gap-x-5 gap-y-3">
            <img src="/brand/hsk-logo.png" alt="HSK" className="h-8 w-auto md:h-11" />
            <span className="h-6 w-[1px] bg-[#2a2a2a]" />
            <span className="font-display text-lg font-extrabold uppercase tracking-[0.04em] text-[#d4ff00] md:text-2xl">
              VIDEO EDITOR
            </span>
          </div>
          <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1">
            <span className="label text-[#a3a3a3]">HARSH KOTWANI</span>
            <span data-testid="hero-identity-location" className="label text-[#7a7a7a]">{CONTACT.location}</span>
            <span className="label text-[#7a7a7a]">CREATORS + BRANDS</span>
          </div>
        </motion.div>

        <div className="max-w-[1500px]">
          <h1 className="font-display text-[11.5vw] font-black uppercase leading-[0.87] tracking-[-0.045em] text-[#e5e5e5] sm:text-[9vw] lg:text-[6.1vw]">
            {["MAKE IT", "IMPOSSIBLE TO", "SCROLL PAST."].map((line, i) => (
              <span key={line} className="mask-line">
                <motion.span
                  className="block"
                  initial={{ y: "112%", skewY: 4 }}
                  animate={{ y: 0, skewY: 0 }}
                  transition={{ duration: 1.05, ease: EASE, delay: D + i * 0.09 }}
                >
                  {i === 2 ? (
                    <>
                      SCROLL PAST<span className="text-[#d4ff00]">.</span>
                    </>
                  ) : (
                    line
                  )}
                </motion.span>
              </span>
            ))}
          </h1>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, ease: EASE, delay: D + 0.4 }}
          className="mt-9 flex flex-col gap-8 border-t border-[#1f1f1f] pt-7 md:flex-row md:items-end md:justify-between"
        >
          <p className="max-w-lg text-sm leading-relaxed text-[#a3a3a3] md:text-base">
            Video editing, motion and visual storytelling for creators and
            brands. <span className="text-[#e5e5e5]">Good footage is only the starting point.</span>
          </p>

          <div className="flex flex-wrap items-center gap-3">
            <a
              href="#reel"
              data-cursor="play"
              data-testid="hero-watch-reel"
              className="group flex items-center gap-3 bg-[#d4ff00] px-7 py-4 transition-opacity duration-300 hover:opacity-85"
            >
              <span className="label text-[#0a0a0a]">WATCH REEL</span>
              <span className="text-[#0a0a0a] transition-transform duration-300 group-hover:translate-x-1">
                →
              </span>
            </a>
            <button
              type="button"
              onClick={onBrief}
              data-testid="hero-start-project"
              className="group relative overflow-hidden border border-[#d4ff00] px-7 py-4"
            >
              <span className="absolute inset-0 translate-y-full bg-[#d4ff00] transition-transform duration-500 ease-swift group-hover:translate-y-0" />
              <span className="relative label text-[#d4ff00] transition-colors duration-300 group-hover:text-[#0a0a0a]">
                START A PROJECT →
              </span>
            </button>
            <a
              href="#work"
              data-cursor="view"
              data-testid="hero-view-work"
              className="group flex items-center gap-3 border border-[#262626] px-7 py-4 transition-colors duration-300 hover:border-[#a3a3a3]"
            >
              <span className="label text-[#a3a3a3] transition-colors group-hover:text-[#e5e5e5]">
                VIEW WORK
              </span>
              <span className="text-[#a3a3a3] transition-transform duration-300 group-hover:translate-y-1">
                ↓
              </span>
            </a>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1, delay: D + 0.7 }}
          className="mt-7 flex items-center justify-between"
        >
          <span className="font-mono text-[11px] tracking-[0.18em] text-[#d4ff00]">
            {tc}
          </span>
          <span data-testid="hero-worldwide" className="label text-[#4d4d4d]">{CONTACT.availability}</span>
        </motion.div>
      </div>
    </section>
  );
};
